import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const navLinks = [
  { label: "Services", href: "/services", dropdown: [
    { label: "Mutual Funds", href: "/mutual-funds" },
    { label: "Insurance", href: "/insurance" },
    { label: "FDs & Bonds", href: "/fd-bonds" },
    { label: "PMS, SIF & Unlisted", href: "/pms-sif" },
  ]},
  { label: "Calculators", href: "/calculators", dropdown: [
    { label: "SIP Calculator", href: "/calculators" },
    { label: "Retirement Planner", href: "/calculators?type=retirement" },
    { label: "Crorepati Calculator", href: "/calculators?type=crorepati" },
          { label: "Child Education", href: "/calculators?type=education" },
    { label: "WealthVitals Score", href: "/wealthvitals" },
  ]},
  { label: "MF Returns", href: "/mutual-fund-returns" },
  { label: "Blog", href: "/blog" },
  { label: "Partners", href: "/partners" },
  { label: "StethoWealth", href: "/stethowealth", isSpecial: true },
];

export default function Navbar({ onStartInvesting }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => { setOpen(false); setDropdown(null); }, [location]);

  return (
    <nav data-testid="navbar" style={{
      position: "sticky", top: 0, zIndex: 999, background: "white",
      boxShadow: scrolled ? "0 4px 24px rgba(27,43,107,0.10)" : "0 1px 0 #E2E8F0",
      transition: "box-shadow 0.3s"
    }}>
      {/* Main Bar */}
      <div className="container" style={{ display: "flex", alignItems: "center", height: 68, gap: 24 }}>
        {/* Logo */}
        <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0, outline: "none" }}>
          <img
            src="/logo-new.png"
            alt="Finadore"
            style={{ height: 42, width: "auto", objectFit: "contain", maxWidth: 170, display: "block", border: "none" }}
          />
        </Link>

        {/* Desktop Nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }} className="desktop-nav">
          {navLinks.map(link => (
            <div key={link.href} style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setDropdown(link.href)}
              onMouseLeave={() => setDropdown(null)}
            >
              <Link to={link.href} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g,"-")}`}
                style={{
                  display: "flex", alignItems: "center", gap: 4, padding: "7px 11px", borderRadius: 8,
                  textDecoration: "none", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
                  whiteSpace: "nowrap", transition: "all 0.15s",
                  color: link.isSpecial ? "var(--green-dark)" : (location.pathname === link.href ? "var(--navy)" : "#475569"),
                  background: location.pathname === link.href ? "var(--navy-50)" : "transparent",
                  border: link.isSpecial ? "1.5px solid var(--green)" : "none",
                }}>
                {link.label}
                {link.dropdown && <ChevronDown size={13} />}
              </Link>
              {link.dropdown && dropdown === link.href && (
                <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, paddingTop: 6 }}>
                  <div style={{ background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", border: "1px solid var(--border)", padding: 8, minWidth: 190 }}>
                    {link.dropdown.map(item => (
                      <Link key={item.href} to={item.href} style={{ display: "block", padding: "9px 14px", borderRadius: 8, textDecoration: "none", fontSize: 14, color: "var(--text)", fontFamily: "DM Sans, sans-serif", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right Actions */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }} className="desktop-nav">
          {/* App download — icon only */}
          <div style={{ display: "flex", gap: 6, marginRight: 4 }}>
            <a href="https://apps.apple.com/gb/app/finadore/id6615080710" target="_blank" rel="noopener noreferrer" title="Download on App Store"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 8, background: "#111", textDecoration: "none" }}>
              <svg width="16" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.finadore.finadore" target="_blank" rel="noopener noreferrer" title="Get on Google Play"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 8, background: "#111", textDecoration: "none" }}>
              <svg width="15" height="17" viewBox="0 0 24 24" fill="none">
                <path d="M2.05 1.38C2.02 1.49 2 1.6 2 1.72v20.56c0 .12.02.23.05.33L13.04 12 2.05 1.38z" fill="#4285F4"/>
                <path d="M3.18 23.76c.3.17.65.19.99.08l12.49-7.2-2.76-2.76-10.72 9.88z" fill="#34A853"/>
                <path d="M20.52 10.3l-2.54-1.46-3.14 3.14 3.14 3.14 2.55-1.47c.73-.42.73-1.53-.01-1.95z" fill="#FBBC04"/>
                <path d="M4.17.16C3.83.05 3.48.07 3.18.24L13.04 10.1l2.76-2.76L4.17.16z" fill="#EA4335"/>
              </svg>
            </a>
          </div>
          <button onClick={onStartInvesting} className="btn-primary" style={{ padding: "7px 16px", fontSize: 13, cursor: "pointer", border: "none" }} data-testid="nav-get-started-btn">
            Get Started
          </button>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle"
          style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 8, color: "var(--navy)", marginLeft: "auto" }}
          className="mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div style={{ background: "white", borderTop: "1px solid var(--border)", padding: "12px 24px 24px" }}>
          {navLinks.map(link => (
            <Link key={link.href} to={link.href} data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g,"-")}`}
              style={{ display: "block", padding: "11px 0", borderBottom: "1px solid var(--border)", textDecoration: "none", fontWeight: 600, fontSize: 15, color: link.isSpecial ? "var(--green-dark)" : "var(--navy)", fontFamily: "DM Sans, sans-serif" }}>
              {link.label}
            </Link>
          ))}
          {/* Mobile app links — icon only */}
          <div style={{ display: "flex", gap: 10, marginTop: 16, marginBottom: 12 }}>
            <a href="https://apps.apple.com/gb/app/finadore/id6615080710" target="_blank" rel="noopener noreferrer" title="Download on App Store"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 10, background: "#111", textDecoration: "none" }}>
              <svg width="18" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.finadore.finadore" target="_blank" rel="noopener noreferrer" title="Get on Google Play"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 10, background: "#111", textDecoration: "none" }}>
              <svg width="16" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M2.05 1.38C2.02 1.49 2 1.6 2 1.72v20.56c0 .12.02.23.05.33L13.04 12 2.05 1.38z" fill="#4285F4"/>
                <path d="M3.18 23.76c.3.17.65.19.99.08l12.49-7.2-2.76-2.76-10.72 9.88z" fill="#34A853"/>
                <path d="M20.52 10.3l-2.54-1.46-3.14 3.14 3.14 3.14 2.55-1.47c.73-.42.73-1.53-.01-1.95z" fill="#FBBC04"/>
                <path d="M4.17.16C3.83.05 3.48.07 3.18.24L13.04 10.1l2.76-2.76L4.17.16z" fill="#EA4335"/>
              </svg>
            </a>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { setOpen(false); onStartInvesting?.(); }} className="btn-primary" style={{ flex: 1, textAlign: "center", justifyContent: "center", padding: "10px", fontSize: 14, cursor: "pointer", border: "none" }}>
              Get Started
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 1000px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
        @media (max-width: 480px) {
          .nav-container-inner { height: 60px !important; }
        }
      `}</style>
    </nav>
  );
}
